import baseQueryWithReauth from '@app/helper/baseQueryWithReAuth';
import {
  TFilterArgs,
  TInventoryDetail,
  TInventoryMedicine,
  TInventoryList,
  TInventoryMedicineUpdate,
  TInventoryUpsertResponse,
  TPaginationArgs,
  TSortByArgs,
  TInventoryMedicineCreate,
} from '@app/types';
import {createApi} from '@reduxjs/toolkit/query/react';

export const inventoryService = createApi({
  reducerPath: 'inventoryService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Inventory'],
  endpoints(builder) {
    return {
      addInventoryMedicine: builder.mutation<
        TInventoryMedicine[],
        {date: string; body: TInventoryMedicineCreate[]}
      >({
        query: ({date, body}) => ({
          url: `/inventories/${date}/medicines`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, {date}) => [
          {type: 'Inventory', id: date},
          {type: 'Inventory', id: 'LIST'},
        ],
      }),
      listInventory: builder.query<
        TInventoryList,
        TPaginationArgs & TSortByArgs & TFilterArgs
      >({
        query: (args) => ({
          url: '/inventories',
          params: {
            ...args,
          },
        }),
        providesTags: [{type: 'Inventory', id: 'LIST'}],
      }),

      getInventory: builder.query<
        TInventoryDetail,
        Partial<TPaginationArgs> & {searchText?: string} & {date: string}
      >({
        query: ({limit, page, date, searchText}) => ({
          url: `/inventories/${date}`,
          params: {
            ...(page && {page}),
            ...(limit && {limit}),
            ...(searchText && {'search_keyword:like': searchText}),
          },
        }),
        providesTags: (_result, _error, {date}) => [
          {type: 'Inventory', id: date},
        ],
      }),

      getInventoryMedicine: builder.query<
        TInventoryMedicine,
        {date: string; id: string}
      >({
        query: ({date, id}) => ({
          url: `/inventories/${date}/medicines/${id}`,
        }),
        providesTags: (_result, _error, {date, id}) => [
          {type: 'Inventory', id: date},
          {type: 'Inventory', id: `${date}-${id}`},
        ],
      }),

      updateInventoryMedicine: builder.mutation<
        TInventoryMedicine,
        {date: string; id: string; body: TInventoryMedicineUpdate}
      >({
        query: ({date, id, body}) => ({
          url: `/inventories/${date}/medicines/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, {date, id}) => [
          {type: 'Inventory', id: date},
          {type: 'Inventory', id: `${date}-${id}`},
        ],
      }),

      upsertInventory: builder.mutation<
        TInventoryUpsertResponse,
        {date: string; body: {memo: string}}
      >({
        query: ({date, body}) => ({
          url: `/inventories/${date}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (_result, _error, {date}) => [
          {type: 'Inventory', id: date},
          {type: 'Inventory', id: 'LIST'},
        ],
      }),

      deleteInventoryMedicine: builder.mutation<
        void,
        {id: string; date: string}
      >({
        query: ({id, date}) => ({
          url: `/inventories/${date}/medicines/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, {date, id}) => [
          {type: 'Inventory', id: date},
          {type: 'Inventory', id: `${date}-${id}`},
        ],
      }),

      deleteInventory: builder.mutation<void, string>({
        query: (date) => ({
          url: `/inventories/${date}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, date) => [
          {type: 'Inventory', id: date},
          {type: 'Inventory', id: 'LIST'},
        ],
      }),
    };
  },
});

export const {
  useAddInventoryMedicineMutation,
  useListInventoryQuery,
  useGetInventoryQuery,
  useLazyGetInventoryQuery,
  useGetInventoryMedicineQuery,
  useUpdateInventoryMedicineMutation,
  useUpsertInventoryMutation,
  useDeleteInventoryMedicineMutation,
  useDeleteInventoryMutation,
} = inventoryService;
